import type React from 'react';
import { useMemo } from 'react';

import { ChartCard } from 'components/ChartCard';
import ChartService from 'services/chart/chartService';
import type { ChartObject, ChartTrendSeries, CompareChartGraphDataItem } from 'services/chart/types';

import { CompareChart } from './CompareChart';
import { GraphChart } from './GraphChart';
import { StyledChartCard } from './styles';

const SUPPORTED_CHART_TYPES = new Set<ChartObject['chart']['type']>(['bar', 'line', 'column']);

export const AIAgentEmailGraphContainer = ({
  chart,
  scale,
  overlay,
}: {
  chart?: Record<string, unknown> | null;
  scale?: number;
  overlay?: React.ReactNode;
}) => {
  const isChartObject = ChartService.isChartObject(chart);
  const isCompareChart = useMemo(() => isChartObject && ChartService.isCompareChart(chart), [chart, isChartObject]);

  const transformedChartData: ChartTrendSeries[] | CompareChartGraphDataItem[] = useMemo(
    () =>
      isChartObject
        ? isCompareChart
          ? ChartService.transformToCompareChart(chart)
          : ChartService.transformToHighchartsSeries(chart)
        : [],
    [chart, isChartObject, isCompareChart]
  );

  if (!isChartObject) {
    return null;
  }

  const type = chart.chart.type;
  const hasData = transformedChartData.length > 0;
  const isSupportedType = SUPPORTED_CHART_TYPES.has(type);

  if (!isSupportedType || !hasData) {
    return null;
  }

  const { title, xargs, series } = chart;
  return (
    <StyledChartCard isCompareChart={isCompareChart} scale={scale} id="ai-outreach-chart">
      <ChartCard
        title={title?.text}
        date={xargs?.date}
        withBorder={true}
        overlay={overlay}
        countryCode={Number(xargs?.country)}
      >
        {isCompareChart ? (
          <CompareChart
            chartData={transformedChartData as CompareChartGraphDataItem[]}
            format={series?.[0]?.dataFormat ?? 'number'}
          />
        ) : (
          <GraphChart chartObject={chart} chartData={transformedChartData} />
        )}
      </ChartCard>
    </StyledChartCard>
  );
};
